import type { ISelectOption } from 'stories/components/SelectOptionInputs/types'

import { fullStates } from './states.constants'

/**
 * Current loan State codes, in dropdown order. Matches `loanOptionsConstants.states`.
 * Puerto Rico is not on this list until a customer checks it.
 */
export const PROPERTY_STATE_DEFAULT_CODES: string[] = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'MA',
  'ME',
  'MD',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VA',
  'VI',
  'VT',
  'WA',
  'WV',
  'WI',
  'WY',
]

const DEFAULT_CODE_SET = new Set(PROPERTY_STATE_DEFAULT_CODES)

/**
 * Checklist catalog: the current State codes plus Puerto Rico.
 * Labels are for the admin screen; the dropdown still shows the two-letter code.
 */
export const PROPERTY_STATE_CATALOG: Array<{ key: string; title: string }> = (() => {
  const codes = [...PROPERTY_STATE_DEFAULT_CODES]
  if (!codes.includes('PR')) {
    const paIndex = codes.indexOf('PA')
    codes.splice(paIndex >= 0 ? paIndex + 1 : codes.length, 0, 'PR')
  }
  return codes.map((key) => ({
    key,
    title: `${(fullStates as Record<string, string>)[key] || key} (${key})`,
  }))
})()

const catalogOrderIndex = new Map(PROPERTY_STATE_CATALOG.map((item, index) => [item.key, index]))

const defaultVisible = (key: string) => DEFAULT_CODE_SET.has(key)

const materializeOptions = (stored?: ISelectOption[] | null): ISelectOption[] => {
  const byKey = new Map((stored || []).filter((opt) => opt?.key).map((opt) => [opt.key, opt]))
  return PROPERTY_STATE_CATALOG.map((item) => {
    const saved = byKey.get(item.key)
    if (saved) {
      return {
        ...saved,
        key: item.key,
        title: item.title,
        visible: saved.visible !== false,
        isCustom: false,
      }
    }
    return {
      key: item.key,
      title: item.title,
      visible: defaultVisible(item.key),
      isCustom: false,
    }
  })
}

/** Admin checklist: full catalog sorted by label, with defaults when nothing is saved. */
export const propertyStateChecklist = (stored?: ISelectOption[] | null): ISelectOption[] => {
  return [...materializeOptions(stored)].sort((a, b) => a.title.localeCompare(b.title))
}

/**
 * Codes offered on the loan State dropdown.
 * No saved list keeps today’s codes (no PR). A saved list keeps checked codes in catalog order.
 * `currentValue` stays in the list when it is a catalog code that was later unchecked.
 */
export const propertyStateSelectOptions = (
  stored?: ISelectOption[] | null,
  currentValue?: string | null,
): string[] => {
  const hasStored = Array.isArray(stored) && stored.length > 0
  let codes: string[]

  if (!hasStored) {
    codes = [...PROPERTY_STATE_DEFAULT_CODES]
  } else {
    const visible = new Set(
      materializeOptions(stored)
        .filter((opt) => opt.visible !== false)
        .map((opt) => opt.key),
    )
    codes = PROPERTY_STATE_CATALOG.map((item) => item.key).filter((key) => visible.has(key))
  }

  const current = String(currentValue || '').trim().toUpperCase()
  if (current && catalogOrderIndex.has(current) && !codes.includes(current)) {
    const insertAt = codes.findIndex((code) => (catalogOrderIndex.get(code) ?? 0) > (catalogOrderIndex.get(current) ?? 0))
    if (insertAt < 0) codes.push(current)
    else codes.splice(insertAt, 0, current)
  }

  return codes
}

/** Toggle one state. First toggle writes the full catalog. The last checked state cannot turn off. */
export const togglePropertyStateVisible = (
  stored: ISelectOption[] | null | undefined,
  key: string,
  visible: boolean,
): ISelectOption[] => {
  const next = materializeOptions(stored)
  if (!visible) {
    const checkedCount = next.filter((opt) => opt.visible !== false).length
    const target = next.find((opt) => opt.key === key)
    if (target && target.visible !== false && checkedCount <= 1) return stored && stored.length ? stored : next
  }
  return next.map((opt) => (opt.key === key ? { ...opt, visible } : opt))
}
